import { API } from './index'
import type {
  CapacityServiceDto,
  CapacityAssignmentServiceDto,
  CapacitySibItemDto,
  CapacitySibDetailDto,
  CapacityBankItemDto,
  CapacityBankDetailDto,
  CapacityAssignmentDto,
} from './dto/capacity.dto'
import type {
  SibCapacityRow,
  BankCapacityRow,
  CapacityAssignment,
  CapacityStatus,
} from '../pages/union/capacity/capacity.types'
import { PILOT_BANK } from '../pages/union/capacity/capacity.utils'

type PagedResponse<T> = {
  items?: T[]
  content?: T[]
  page?: number
  pageSize?: number
  totalElements?: number
  totalItems?: number
  totalPages?: number
}

function extractPage<T>(res: T[] | PagedResponse<T>): {
  items: T[]
  totalPages: number
  pageSize: number
} {
  if (Array.isArray(res)) return { items: res, totalPages: 1, pageSize: res.length }
  const items = res.items ?? res.content ?? []
  const pageSize = res.pageSize ?? (items.length || 25)
  const total = res.totalElements ?? res.totalItems ?? items.length
  const totalPages = res.totalPages ?? Math.ceil(total / pageSize)
  return { items, totalPages, pageSize }
}

export interface CapacityPageResult<T> {
  items: T[]
  totalPages: number
  totalElements: number
}

async function fetchOnePage<T>(path: string): Promise<CapacityPageResult<T>> {
  const res = await API<T[] | PagedResponse<T>>(path)
  const { items, totalPages } = extractPage(res)
  const totalElements = Array.isArray(res)
    ? res.length
    : ((res as PagedResponse<T>).totalElements ??
      (res as PagedResponse<T>).totalItems ??
      items.length)
  return { items, totalPages, totalElements }
}

export function getCapacitySibsPage(
  page: number,
  year?: number
): Promise<CapacityPageResult<CapacitySibItemDto>> {
  const extra = year ? `&year=${year}` : ''
  return fetchOnePage<CapacitySibItemDto>(`/capacity/sibs?page=${page}${extra}`)
}

export function getCapacitySib(userId: string, year?: number): Promise<CapacitySibDetailDto> {
  const qs = year ? `?year=${year}` : ''
  return API<CapacitySibDetailDto>(`/capacity/sibs/${encodeURIComponent(userId)}${qs}`)
}

export function getCapacityBanksPage(
  page: number,
  year?: number
): Promise<CapacityPageResult<CapacityBankItemDto>> {
  const extra = year ? `&year=${year}` : ''
  return fetchOnePage<CapacityBankItemDto>(`/capacity/banks?page=${page}${extra}`)
}

export function getCapacityBank(
  customerId: number,
  year?: number
): Promise<CapacityBankDetailDto> {
  const qs = year ? `?year=${year}` : ''
  return API<CapacityBankDetailDto>(`/capacity/banks/${customerId}${qs}`)
}

// ---- helpers ----

function pctString(decimal: number | null | undefined): string {
  return `${Math.round((decimal ?? 0) * 100)} %`
}

function formatGermanCurrency(amount: number): string {
  return `${Math.round(amount).toLocaleString('de-DE')} EUR`
}

function svc(services: CapacityServiceDto[], code: string): CapacityServiceDto | undefined {
  return services.find(s => s.serviceCode === code)
}

function assignmentSvc(
  services: CapacityAssignmentServiceDto[],
  code: string
): CapacityAssignmentServiceDto | undefined {
  return services.find(s => s.serviceCode === code)
}

// ---- adapters ----

export function adaptSibItem(dto: CapacitySibItemDto): SibCapacityRow {
  const toj = svc(dto.services ?? [], 'TOJ')
  const consulting = svc(dto.services ?? [], 'CONSULTING')
  const training = svc(dto.services ?? [], 'TRAINING')
  const pilot = svc(dto.services ?? [], 'PILOT')
  return {
    userId: dto.userId ?? '',
    status: (dto.status ?? 'green').toLowerCase() as CapacityStatus,
    sib: dto.fullName ?? '',
    place: [dto.postal, dto.city].filter(Boolean).join(' '),
    region: dto.salesRegion ?? '',
    radius: dto.workRadius ?? '',
    frame: [
      String(toj?.capacityUnits ?? 0),
      String(consulting?.capacityUnits ?? 0),
      String(training?.capacityUnits ?? 0),
      String(pilot?.capacityUnits ?? 0),
    ],
    ordered: [
      String(toj?.orderedUnits ?? 0),
      String(consulting?.orderedUnits ?? 0),
      String(training?.orderedUnits ?? 0),
      String(pilot?.orderedUnits ?? 0),
    ],
    usage: pctString(dto.usagePct),
    target: pctString(dto.zeitzielIndex),
    revenue: formatGermanCurrency(dto.totalBudgetPlan),
    contacts: dto.contacts,
    topics: dto.topics,
  }
}

export function adaptBankItem(dto: CapacityBankItemDto): BankCapacityRow {
  const toj = svc(dto.services ?? [], 'TOJ')
  const consulting = svc(dto.services ?? [], 'CONSULTING')
  const training = svc(dto.services ?? [], 'TRAINING')
  const pilot = svc(dto.services ?? [], 'PILOT')
  const showPilot = (dto.name ?? '') === PILOT_BANK
  return {
    customerId: dto.customerId,
    status: (dto.status ?? 'green').toLowerCase() as CapacityStatus,
    bank: dto.name ?? '',
    showPilot,
    blz: dto.blzNum ?? '',
    place: [dto.postal, dto.city].filter(Boolean).join(' '),
    region: dto.salesRegion ?? '',
    ordered: [
      String(toj?.orderedUnits ?? 0),
      String(consulting?.orderedUnits ?? 0),
      String(training?.orderedUnits ?? 0),
      ...(showPilot ? [String(pilot?.orderedUnits ?? 0)] : []),
    ],
    done: [
      String(toj?.doneUnits ?? 0),
      String(consulting?.doneUnits ?? 0),
      String(training?.doneUnits ?? 0),
      ...(showPilot ? [String(pilot?.doneUnits ?? 0)] : []),
    ],
    target: pctString(dto.zeitzielIndex),
    sibs: String(dto.activeSibCount),
    marketplace: '',
  }
}

export function adaptAssignment(a: CapacityAssignmentDto): CapacityAssignment {
  const toj = assignmentSvc(a.services, 'TOJ')
  const consulting = assignmentSvc(a.services, 'CONSULTING')
  const training = assignmentSvc(a.services, 'TRAINING')
  const pilot = assignmentSvc(a.services, 'PILOT')
  return {
    sib: a.sibName,
    bank: a.customerName,
    until: a.latestOrderEndTime,
    ordered: [
      String(toj?.orderedUnits ?? 0),
      String(consulting?.orderedUnits ?? 0),
      String(training?.orderedUnits ?? 0),
      String(pilot?.orderedUnits ?? 0),
    ],
    done: [
      String(toj?.doneUnits ?? 0),
      String(consulting?.doneUnits ?? 0),
      String(training?.doneUnits ?? 0),
      String(pilot?.doneUnits ?? 0),
    ],
    target: [
      pctString(toj?.zeitzielIndex ?? 0),
      pctString(consulting?.zeitzielIndex ?? 0),
      pctString(training?.zeitzielIndex ?? 0),
      pctString(pilot?.zeitzielIndex ?? 0),
    ],
  }
}
